import { useCallback, useEffect, useState } from "react";
import { get, post } from "../lib/api";
import { Card, Button, Empty } from "../lib/ui";

/**
 * A agenda do corretor dentro do painel.
 *
 * Duas peças, em lugares diferentes de propósito: conectar e escolher a agenda
 * é configuração e mora nos ajustes; ver o que está marcado é operação e mora
 * na tela de Agenda, ao lado do que a IA agendou.
 *
 * A visualização lê o calendário DE VERDADE, não só a nossa tabela. O corretor
 * marca coisa pelo celular, e uma tela que mostrasse apenas o que a IA marcou
 * daria a impressão de uma semana vazia numa semana cheia — e é exatamente
 * nessa impressão que se marca em cima de um compromisso existente.
 */

interface Estado {
  configurado: boolean;
  conectado: boolean;
  email: string | null;
  calendarId: string | null;
  calendarNome: string | null;
  calendariosConsulta: string[];
  emailsBloqueiam: string[];
}

interface Teste {
  consultadas: string[];
  filtrandoPor: string[];
  ocupacoes: number;
  falharam: { calendario: string; motivo: string }[];
}

interface Calendario {
  id: string;
  nome: string;
  principal: boolean;
}

interface Evento {
  id: string;
  titulo: string;
  inicio: string;
  fim: string;
  /**
   * Preenchido quando o horário nasceu aqui.
   *
   * Nulo = o corretor marcou por fora (celular, computador, outra pessoa).
   * O Google não distingue os dois: o evento é criado com a conta dele nos
   * dois casos. Quem sabe é o nosso banco, pelo id do evento.
   */
  axis: {
    leadId: string;
    leadNome: string | null;
    leadTelefone: string;
    modo: "ligacao" | "video" | "presencial";
    status: string;
    contexto: string | null;
  } | null;
  criador: Pessoa | null;
  organizador: Pessoa | null;
  /** De qual agenda este evento veio. */
  agenda: string;
}

interface Hora {
  inicio: string;
  fim: string;
  livre: boolean;
  passou: boolean;
  motivo: { titulo?: string; quem?: string } | null;
}

interface Disponibilidade {
  grade: { dia: string; horas: Hora[] }[];
  consultadas: string[];
  filtrandoPor: string[];
  falharam: { calendario: string; motivo: string }[];
  comDetalhe: boolean;
}

interface Pessoa {
  nome: string | null;
  email: string | null;
  /** É o dono desta agenda — ou seja, foi ele mesmo que marcou. */
  souEu: boolean;
}

/**
 * Quem marcou este horário, em uma frase.
 *
 * A ordem importa. O selo do Axis vem primeiro porque é o único caso em que o
 * Google mente por omissão: o evento é criado com a conta do corretor, então
 * ele consta como criador do que a IA agendou. Só depois disso vale olhar o
 * campo do Google.
 *
 * O organizador entra quando difere do criador — é o caso do convite que veio
 * de fora, onde quem manda na reunião é a outra pessoa.
 */
function quemMarcou(e: Evento): string {
  if (e.axis) return "";
  const c = e.criador;
  if (!c) return "origem desconhecida";
  if (c.souEu) {
    const org = e.organizador;
    if (org && !org.souEu) {
      return `você aceitou — organiza ${org.nome ?? org.email ?? "outra pessoa"}`;
    }
    return "marcado por você";
  }
  return `marcado por ${c.nome ?? c.email ?? "outra conta"}`;
}

/** Como cada modo aparece no selo da agenda. */
const MODO_ROTULO: Record<string, string> = {
  ligacao: "ligação",
  video: "vídeo",
  presencial: "presencial",
};

function useEstado() {
  const [estado, setEstado] = useState<Estado | null>(null);
  const recarregar = useCallback(() => {
    get<Estado>("/google/estado")
      .then(setEstado)
      .catch(() => setEstado(null));
  }, []);
  useEffect(recarregar, [recarregar]);
  return { estado, recarregar };
}

/** Ajustes: conectar a conta e escolher em qual agenda caem as visitas. */
export function ConexaoGoogle() {
  const { estado, recarregar } = useEstado();
  const [calendarios, setCalendarios] = useState<Calendario[] | null>(null);
  const [erroLista, setErroLista] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  /*
   * O aviso de volta do Google.
   *
   * O callback redireciona para cá com `?agenda=...`. Sem ler isso, quem
   * autorizou voltaria para um painel idêntico ao que deixou e não saberia se
   * deu certo -- e tentaria de novo.
   */
  const [aviso, setAviso] = useState<string | null>(null);
  useEffect(() => {
    const r = new URLSearchParams(location.search).get("agenda");
    if (!r) return;
    setAviso(
      {
        ok: "Agenda conectada.",
        cancelado: "Você cancelou a autorização.",
        invalido: "O retorno do Google não conferiu. Tente de novo.",
        falhou: "O Google recusou a autorização. Tente de novo.",
      }[r] ?? null,
    );
    // Limpa a URL para o aviso não reaparecer a cada F5.
    history.replaceState(null, "", location.pathname);
  }, []);

  useEffect(() => {
    if (!estado?.conectado) return;
    // O erro aparece na tela. Cair em lista vazia escondia a causa e fazia
    // parecer que a conta não tinha agenda nenhuma.
    fetch("/api/google/calendarios")
      .then(async (r) => {
        const corpo = await r.json();
        if (!r.ok) throw new Error(corpo?.error ?? `erro ${r.status}`);
        setCalendarios(corpo as Calendario[]);
        setErroLista(null);
      })
      .catch((e: Error) => {
        setCalendarios([]);
        setErroLista(e.message);
      });
  }, [estado?.conectado]);

  if (!estado) return null;

  if (!estado.configurado) {
    return (
      <Card className="p-4">
        <div className="text-sm text-ink">Agenda do Google</div>
        <p className="text-sm text-ink-3 mt-2">
          Falta a credencial no servidor. No Google Cloud, ative a Calendar API,
          crie uma credencial OAuth do tipo <em>Aplicativo da Web</em> e
          preencha <code>GOOGLE_CLIENT_ID</code>,{" "}
          <code>GOOGLE_CLIENT_SECRET</code> e <code>GOOGLE_REDIRECT_URI</code>.
        </p>
      </Card>
    );
  }

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="text-sm text-ink">Agenda do Google</div>
          <div className="text-sm text-ink-3 mt-1">
            {estado.conectado
              ? `Conectada${estado.email ? ` — ${estado.email}` : ""}`
              : "A IA só oferece horários que estiverem livres aqui."}
          </div>
        </div>
        {estado.conectado ? (
          <Button
            variant="danger"
            onClick={async () => {
              await post("/google/desconectar");
              recarregar();
            }}
          >
            Desconectar
          </Button>
        ) : (
          <Button
            variant="primary"
            onClick={() => {
              location.href = "/api/google/conectar";
            }}
          >
            Conectar agenda
          </Button>
        )}
      </div>

      {aviso && <div className="text-sm text-ink-2 mt-3">{aviso}</div>}

      {estado.conectado && (
        <div className="mt-4">
          <label className="text-sm text-ink-3 block mb-1">
            Onde as visitas são marcadas
          </label>
          <select
            className="bg-surface-2 border border-rule-2 rounded-lg px-2 py-1.5 text-sm text-ink w-full"
            value={estado.calendarId ?? "primary"}
            disabled={salvando || !calendarios}
            onChange={async (ev) => {
              const id = ev.target.value;
              const nome = calendarios?.find((c) => c.id === id)?.nome ?? null;
              setSalvando(true);
              await post("/google/calendario", { id, nome }).catch(() => {});
              setSalvando(false);
              recarregar();
            }}
          >
            {(calendarios ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.nome}
                {c.principal ? " (principal)" : ""}
              </option>
            ))}
          </select>
          {erroLista && (
            <p className="text-sm text-danger mt-2">{erroLista}</p>
          )}
          {!erroLista && calendarios?.length === 0 && (
            <p className="text-sm text-ink-3 mt-2">
              Nenhuma agenda gravável nesta conta.
            </p>
          )}
          <p className="text-sm text-ink-3 mt-2">
            Só aparecem agendas em que dá para criar evento. O evento nasce
            aqui e só aqui — um agendamento que aparece em duas agendas vira
            dois compromissos para quem olha de fora.
          </p>

          <AgendasDeConsulta
            estado={estado}
            calendarios={calendarios ?? []}
            onMudou={recarregar}
          />

          <CorretoresQueBloqueiam estado={estado} onMudou={recarregar} />
        </div>
      )}
    </Card>
  );
}

const DIA = (iso: string) =>
  new Date(iso).toLocaleDateString("pt-BR", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

const HORA = (iso: string) =>
  new Date(iso).toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  });

/** Tela de Agenda: as duas semanas seguintes, como estão no Google. */
export function AgendaDoCorretor() {
  const { estado } = useEstado();
  const [eventos, setEventos] = useState<Evento[] | null>(null);
  const [agenda, setAgenda] = useState<string>("todas");
  const [verGrade, setVerGrade] = useState(false);

  useEffect(() => {
    if (!estado?.conectado) return;
    get<Evento[]>("/google/eventos?dias=14")
      .then(setEventos)
      .catch(() => setEventos([]));
  }, [estado?.conectado]);

  if (!estado?.conectado) return null;

  // Agrupado por dia: uma lista corrida de horários sem quebra de data é
  // exatamente o formato em que se lê "quinta" e se marca na quarta.
  const agendas = [...new Set((eventos ?? []).map((e) => e.agenda))];
  const visiveis = (eventos ?? []).filter(
    (e) => agenda === "todas" || e.agenda === agenda,
  );

  const porDia = new Map<string, Evento[]>();
  for (const e of visiveis) {
    const chave = DIA(e.inicio);
    porDia.set(chave, [...(porDia.get(chave) ?? []), e]);
  }

  return (
    <div className="mb-6">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <span className="text-sm text-ink-3">Agenda</span>
        {agendas.length > 1 && (
          <select
            className="bg-surface-2 border border-rule-2 rounded-lg px-2 py-1 text-sm text-ink"
            value={agenda}
            onChange={(e) => setAgenda(e.target.value)}
          >
            <option value="todas">todas ({agendas.length})</option>
            {agendas.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        )}
        <Button variant="ghost" onClick={() => setVerGrade((v) => !v)}>
          {verGrade ? "ver compromissos" : "ver o que a IA oferece"}
        </Button>
      </div>

      {verGrade && <Grade />}

      {eventos === null ? null : porDia.size === 0 ? (
        <Empty>Nada marcado nas próximas duas semanas.</Empty>
      ) : (
        <div className="space-y-3">
          {[...porDia].map(([dia, doDia]) => (
            <Card key={dia} className="p-3">
              <div className="text-sm text-ink capitalize">{dia}</div>
              <div className="mt-2 space-y-1">
                {doDia.map((e) => (
                  <div key={e.id} className="flex gap-3 text-sm items-baseline">
                    <span className="text-ink-3 tabular-nums shrink-0">
                      {HORA(e.inicio)}–{HORA(e.fim)}
                    </span>
                    <span className="text-ink-2 truncate">{e.titulo}</span>
                    {agenda === "todas" && agendas.length > 1 && (
                      <span className="text-[11px] text-ink-3 shrink-0">
                        {e.agenda}
                      </span>
                    )}
                    {e.axis ? (
                      <span
                        className="text-[11px] px-1.5 py-0.5 rounded-lg bg-accent/15 text-accent shrink-0"
                        title={e.axis.contexto ?? "marcado pela IA"}
                      >
                        Axis · {MODO_ROTULO[e.axis.modo] ?? e.axis.modo}
                      </span>
                    ) : (
                      <span
                        className="text-[11px] text-ink-3 shrink-0"
                        title={e.criador?.email ?? undefined}
                      >
                        {quemMarcou(e)}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Quais agendas contam para dizer que um horário está ocupado.
 *
 * Separado de "onde salvar" porque são perguntas diferentes. Numa imobiliária
 * a agenda é coletiva: o horário das 13h está ocupado se QUALQUER pessoa da
 * equipe tiver compromisso ali. Mas o evento novo não deve nascer na agenda
 * pessoal de cada um — isso seria invadir a agenda deles.
 *
 * Aceita e-mail digitado à mão, e não só item da lista: a agenda de um colega
 * costuma não estar inscrita na sua conta, mas a disponibilidade dele é
 * visível assim mesmo. Restringir à lista deixaria de fora justamente quem
 * mais importa.
 */
function AgendasDeConsulta({
  estado,
  calendarios,
  onMudou,
}: {
  estado: Estado;
  calendarios: Calendario[];
  onMudou: () => void;
}) {
  const [novo, setNovo] = useState("");
  const [teste, setTeste] = useState<Teste | null>(null);
  const [testando, setTestando] = useState(false);

  const atuais = estado.calendariosConsulta;

  async function salvar(ids: string[]) {
    await post("/google/consulta", { ids });
    setTeste(null);
    onMudou();
  }

  // A de gravação é sempre consultada, mesmo sem estar na lista -- não conferir
  // conflito onde o evento vai nascer seria o erro mais óbvio de todos.
  const daGravacao = estado.calendarId;

  return (
    <div className="mt-5 pt-4 border-t border-rule">
      <div className="text-sm text-ink">Agendas comparadas para achar horário</div>
      <p className="text-sm text-ink-3 mt-1 max-w-prose">
        Um horário só é oferecido se estiver livre em <em>todas</em> elas. Serve
        para agenda de equipe: se às 13h alguém já tem compromisso, a IA não
        oferece 13h.
      </p>

      <div className="mt-3 space-y-1">
        {daGravacao && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-ink-2">{daGravacao}</span>
            <span className="text-[11px] text-ink-3">
              sempre comparada — é onde o evento nasce
            </span>
          </div>
        )}
        {atuais
          .filter((id) => id !== daGravacao)
          .map((id) => (
            <div key={id} className="flex items-center gap-2 text-sm">
              <span className="text-ink-2">{id}</span>
              <Button
                variant="ghost"
                className="text-[11px] px-1 py-0"
                onClick={() => salvar(atuais.filter((x) => x !== id))}
              >
                remover
              </Button>
            </div>
          ))}
      </div>

      <div className="flex flex-wrap gap-2 mt-3">
        <select
          className="bg-surface-2 border border-rule-2 rounded-lg px-2 py-1.5 text-sm text-ink"
          value=""
          onChange={(e) => {
            if (e.target.value) salvar([...atuais, e.target.value]);
          }}
        >
          <option value="">adicionar da lista…</option>
          {calendarios
            .filter((c) => !atuais.includes(c.id) && c.id !== daGravacao)
            .map((c) => (
              <option key={c.id} value={c.id}>
                {c.nome}
              </option>
            ))}
        </select>

        <input
          className="bg-surface-2 border border-rule-2 rounded-lg px-2 py-1.5 text-sm text-ink flex-1 min-w-48"
          placeholder="ou o e-mail de um colega"
          value={novo}
          onChange={(e) => setNovo(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && novo.trim()) {
              salvar([...atuais, novo.trim()]);
              setNovo("");
            }
          }}
        />
        <Button
          onClick={() => {
            if (novo.trim()) {
              salvar([...atuais, novo.trim()]);
              setNovo("");
            }
          }}
        >
          Adicionar
        </Button>
      </div>

      <div className="mt-3">
        <Button
          disabled={testando}
          onClick={async () => {
            setTestando(true);
            try {
              setTeste(await get<Teste>("/google/consulta/testar"));
            } catch {
              setTeste(null);
            }
            setTestando(false);
          }}
        >
          {testando ? "testando…" : "Testar leitura"}
        </Button>

        {teste && (
          <div className="mt-2 text-sm">
            {teste.falharam.length === 0 ? (
              <span className="text-ok">
                Todas legíveis — {teste.ocupacoes} compromisso
                {teste.ocupacoes === 1 ? "" : "s"} nas próximas 24h
                {teste.filtrandoPor.length > 0
                  ? ` de ${teste.filtrandoPor.join(", ")}`
                  : " (de qualquer pessoa)"}
                .
              </span>
            ) : (
              <div className="text-danger">
                <div>Não consegui ler estas — os horários delas NÃO estão</div>
                <div>sendo considerados, e a IA pode oferecer horário ocupado:</div>
                <ul className="mt-1">
                  {teste.falharam.map((f) => (
                    <li key={f.calendario}>
                      • {f.calendario} — {f.motivo}
                    </li>
                  ))}
                </ul>
                <div className="text-ink-3 mt-1">
                  Peça à pessoa para compartilhar a agenda com{" "}
                  {estado.email ?? "esta conta"}, ao menos como “ver apenas
                  livre/ocupado”.
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * De quem são os compromissos que bloqueiam um horário.
 *
 * Numa agenda coletiva, a agenda inteira não é o filtro certo. Ela tem quinze
 * marcações por dia de corretores diferentes; o compromisso das 13h de alguém
 * que não vai atender este lead não nos impede de nada, e o das 13h30 de quem
 * vai atender impede.
 *
 * Vazio mantém o comportamento padrão — qualquer compromisso ocupa — e esse é
 * o lado seguro para errar: um horário a menos na oferta custa pouco, um
 * corretor faltando custa o lead.
 */
function CorretoresQueBloqueiam({
  estado,
  onMudou,
}: {
  estado: Estado;
  onMudou: () => void;
}) {
  const [novo, setNovo] = useState("");
  const atuais = estado.emailsBloqueiam;

  async function salvar(emails: string[]) {
    await post("/google/bloqueiam", { emails });
    onMudou();
  }

  return (
    <div className="mt-5 pt-4 border-t border-rule">
      <div className="text-sm text-ink">
        Compromissos de quem bloqueiam o horário
      </div>
      <p className="text-sm text-ink-3 mt-1 max-w-prose">
        Para agenda coletiva. Se a agenda tem marcação de vários corretores, só
        os compromissos <em>destas</em> pessoas impedem a IA de oferecer o
        horário — os demais são ignorados. Deixe vazio para qualquer
        compromisso ocupar.
      </p>

      {atuais.length === 0 ? (
        <p className="text-sm text-ink-3 mt-3">
          Vazio — <strong>qualquer</strong> compromisso na agenda ocupa o
          horário.
        </p>
      ) : (
        <div className="mt-3 space-y-1">
          {atuais.map((e) => (
            <div key={e} className="flex items-center gap-2 text-sm">
              <span className="text-ink-2">{e}</span>
              <Button
                variant="ghost"
                className="text-[11px] px-1 py-0"
                onClick={() => salvar(atuais.filter((x) => x !== e))}
              >
                remover
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2 mt-3">
        <input
          className="bg-surface-2 border border-rule-2 rounded-lg px-2 py-1.5 text-sm text-ink flex-1 min-w-48"
          placeholder="e-mail do corretor"
          value={novo}
          onChange={(ev) => setNovo(ev.target.value)}
          onKeyDown={(ev) => {
            if (ev.key === "Enter" && novo.trim()) {
              salvar([...atuais, novo.trim()]);
              setNovo("");
            }
          }}
        />
        <Button
          onClick={() => {
            if (novo.trim()) {
              salvar([...atuais, novo.trim()]);
              setNovo("");
            }
          }}
        >
          Adicionar
        </Button>
      </div>

      <p className="text-xs text-ink-3 mt-2 max-w-prose">
        Conta como compromisso da pessoa se ela <strong>criou</strong>,
        <strong>organiza</strong> ou <strong>foi convidada</strong> — estar
        convidado ocupa tanto quanto ter marcado. Convite recusado não ocupa.
        Evento sem ninguém identificável conta como ocupado.
        <br />
        Com esta lista preenchida o sistema lê os eventos da agenda para saber
        de quem é cada um; o modo anônimo (livre/ocupado) não carrega essa
        informação.
      </p>
    </div>
  );
}

/**
 * O que a IA vai oferecer, hora a hora.
 *
 * Existe para conferência. Sem ela, saber se o filtro de corretores está
 * funcionando exigia abrir uma conversa de verdade e observar que horário a IA
 * propõe — caro, lento, e com um lead real no meio.
 *
 * Os dados vêm do mesmo cálculo que o agente usa, não de uma cópia: uma
 * segunda implementação divergiria no primeiro ajuste e a tela passaria a
 * mentir sobre o comportamento.
 */
function Grade() {
  const [d, setD] = useState<Disponibilidade | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    fetch("/api/google/disponibilidade?dias=7")
      .then(async (r) => {
        const corpo = await r.json();
        if (!r.ok) throw new Error(corpo?.error ?? `erro ${r.status}`);
        setD(corpo as Disponibilidade);
      })
      .catch((e: Error) => setErro(e.message));
  }, []);

  if (erro) return <p className="text-sm text-danger mb-4">{erro}</p>;
  if (!d) return null;

  return (
    <div className="mb-5">
      <p className="text-xs text-ink-3 mb-2 max-w-prose">
        Verde é horário que a IA pode oferecer. Vermelho ela recusa
        {d.filtrandoPor.length > 0
          ? ` — só contam compromissos de ${d.filtrandoPor.join(", ")}`
          : " — qualquer compromisso ocupa"}
        . Cinza já passou.
      </p>

      {d.falharam.length > 0 && (
        <p className="text-sm text-danger mb-2">
          Agendas não lidas ({d.falharam.map((f) => f.calendario).join(", ")}) —
          os horários delas NÃO estão sendo considerados.
        </p>
      )}

      <div className="space-y-2">
        {d.grade.map((dia) => (
          <div key={dia.dia}>
            <div className="text-xs text-ink-3 capitalize mb-1">
              {DIA(dia.dia)}
            </div>
            <div className="flex flex-wrap gap-1">
              {dia.horas.map((h) => (
                <span
                  key={h.inicio}
                  title={
                    h.passou
                      ? "já passou"
                      : h.livre
                        ? "a IA pode oferecer"
                        : d.comDetalhe
                          ? `ocupado — ${h.motivo?.titulo ?? "compromisso"}${
                              h.motivo?.quem ? ` (${h.motivo.quem})` : ""
                            }`
                          : "ocupado"
                  }
                  className={`text-xs px-1.5 py-0.5 rounded-lg tabular-nums ${
                    h.passou
                      ? "bg-surface-2 text-ink-3"
                      : h.livre
                        ? "bg-ok/15 text-ok"
                        : "bg-danger/15 text-danger"
                  }`}
                >
                  {HORA(h.inicio)}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
